/** Shared replay format (client + server). Format version 1. */

export const REPLAY_FORMAT_VERSION = 1 as const;

/** Max stored payload size (~1.5 MB JSON). */
export const REPLAY_MAX_PAYLOAD_BYTES = 1_500_000;

/** Hard cap on run length accepted for a replay. */
export const REPLAY_MAX_DURATION_MS = 3 * 60 * 60 * 1000;

export type ReplayDevice = "pc" | "mobile";

/** Sparse keyboard / skill / click events (sim time in ms). */
export type ReplayKeyEvent = {
  t: number;
  key: string;
  down: boolean;
};

export type ReplayClickEvent = {
  t: number;
  /** Normalized 0–1 */
  x: number;
  /** Normalized 0–1 */
  y: number;
  button?: number;
};

/**
 * Compact frame stream:
 * - `dtsB64`: Uint8Array, each byte = frame dt in ms clamped to [1, 50]
 * - `ptrB64`: Uint16 LE pairs (nx, ny) scaled 0–65535 for every frame
 */
export type ReplayPayloadV1 = {
  v: typeof REPLAY_FORMAT_VERSION;
  gameVersion: string;
  mode: string;
  metric: string;
  seed: number;
  canvasW: number;
  canvasH: number;
  durationMs: number;
  value: number;
  device: ReplayDevice;
  level?: number;
  presetId?: string;
  /** Base64 of Uint8Array (dt ms per frame). */
  dtsB64: string;
  /** Base64 of Uint16LE nx,ny per frame. */
  ptrB64: string;
  keys?: ReplayKeyEvent[];
  clicks?: ReplayClickEvent[];
};

export type ReplayAvailability = {
  available: boolean;
  reason?: "none" | "incompatible" | "missing";
  gameVersion?: string | null;
  mode?: string;
  metric?: string;
  value?: number;
  userId?: string;
};

export function metricForReplayMode(
  mode: string,
  opts: { level?: number; presetId?: string } = {},
): string | null {
  if (mode === "progression") return "level";
  if (mode === "speedrun") {
    const level = Math.floor(Number(opts.level));
    if (!Number.isInteger(level) || level < 1 || level > 999) return null;
    return `speedrun_lvl${level}`;
  }
  if (mode === "penalty") {
    const level = Math.floor(Number(opts.level));
    if (!Number.isInteger(level) || level < 1 || level > 999) return null;
    return `penalty_lvl${level}`;
  }
  if (mode === "sandbox_speedrun") {
    const presetId = String(opts.presetId || "").trim();
    if (!presetId) return null;
    return `sbpre_${presetId}`;
  }
  if (mode === "sandbox" && String(opts.presetId || "").trim()) {
    return `sbscore_${String(opts.presetId).trim()}`;
  }
  return "score";
}
